<template>
  <div class="admin-page">
    <header class="admin-page__header">
      <div>
        <h1 class="admin-page__title">实体管理</h1>
        <p class="admin-page__subtitle">检索、新建与维护图谱内实体</p>
      </div>
      <div class="admin-page__actions">
        <span class="admin-page__filter-label">类型</span>
        <el-select
          v-model="state.typeCode"
          style="width: 200px"
          placeholder="全部类型"
          clearable
          :disabled="graphId == null"
          @change="handleSearch"
        >
          <el-option
            v-for="t in state.typeOptions"
            :key="t.value"
            :label="t.label"
            :value="t.value"
          />
        </el-select>
        <el-input
          v-model="state.keywords"
          placeholder="搜索名称/别名"
          style="width: 200px"
          clearable
          @keyup.enter="handleSearch"
        />
        <el-button @click="handleSearch">搜索</el-button>
        <el-button
          type="primary"
          :disabled="graphId == null"
          @click="state.createDrawerVisible = true"
        >
          新建实体
        </el-button>
      </div>
    </header>

    <section class="admin-panel admin-panel--entity-table">
      <el-table
        v-loading="state.loading"
        class="admin-entity-table"
        :data="state.list"
        :empty-text="emptyText"
        stripe
        style="width: 100%"
      >
        <el-table-column
          prop="name"
          label="实体名称"
          min-width="60"
          show-overflow-tooltip
        />
        <el-table-column label="别名" width="320">
          <template #default="{ row }">
            <el-tooltip
              v-if="normalizeAliases(row).length"
              :content="formatTagTooltip(normalizeAliases(row))"
              placement="top"
              :show-after="300"
            >
              <div class="admin-entity-table__alias-line">
                <span
                  v-for="(item, index) in displayAliases(row)"
                  :key="`${item}-${index}`"
                  class="admin-entity-table__alias-chip"
                >
                  {{ item }}
                </span>
                <span
                  v-if="extraAliasCount(row) > 0"
                  class="admin-entity-table__alias-more"
                >
                  +{{ extraAliasCount(row) }}
                </span>
              </div>
            </el-tooltip>
            <span v-else class="admin-entity-table__empty">—</span>
          </template>
        </el-table-column>
        <el-table-column
          label="类型"
          min-width="152"
          class-name="admin-entity-table__type-col"
        >
          <template #default="{ row }">
            <el-tooltip
              v-if="normalizeTypes(row).length"
              :content="formatTagTooltip(normalizeTypes(row))"
              placement="top"
              :show-after="300"
            >
              <div class="admin-entity-table__type-line">
                <span
                  v-for="(name, index) in displayTypes(row)"
                  :key="`${name}-${index}`"
                  class="admin-entity-table__type-chip"
                >
                  {{ name }}
                </span>
                <span
                  v-if="extraTypeCount(row) > 0"
                  class="admin-entity-table__type-count"
                >
                  +{{ extraTypeCount(row) }}
                </span>
              </div>
            </el-tooltip>
            <span v-else class="admin-entity-table__empty">—</span>
          </template>
        </el-table-column>
        <el-table-column
          label="操作"
          width="96"
          align="center"
          class-name="admin-table-actions"
        >
          <template #default="{ row }">
            <el-button
              class="admin-entity-table__action"
              size="small"
              type="primary"
              plain
              @click="router.push(`/admin/entities/${row.entityId}`)"
            >
              详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div
        v-if="graphId != null && state.total > 0"
        class="admin-entity-table__footer"
      >
        <el-pagination
          layout="total, prev, pager, next"
          :total="state.total"
          :page-size="state.pageSize"
          :current-page="state.pageNum"
          @current-change="onPageChange"
        />
      </div>
    </section>

    <admin-entity-create-drawer
      v-model="state.createDrawerVisible"
      :default-graph-id="graphId"
      @created="onEntityCreated"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import AdminEntityCreateDrawer from "@/components/entity/AdminEntityCreateDrawer.vue";
import { useAdminSelectedGraph } from "@/composables/useAdminSelectedGraph";
import { listAdminEntities } from "@/api/adminEntityApi";
import { listEntityCategories } from "@/api/adminEntityCategoryApi";

const router = useRouter();
const route = useRoute();
const { graphId, graphOptions, ensureGraphOptionsLoaded, setGraphId } =
  useAdminSelectedGraph();

let syncingFromRoute = false;

function parseGraphIdFromRoute() {
  const raw = route.query.graphId;
  if (raw == null || raw === "") {
    return null;
  }
  const id = Number(raw);
  return Number.isFinite(id) ? id : null;
}

function parseTypeCodeFromRoute() {
  const raw = route.query.typeCode;
  if (raw == null || raw === "") {
    return null;
  }
  return String(raw);
}

async function applyGraphFromRoute() {
  const preferred = parseGraphIdFromRoute();
  if (preferred == null) {
    return false;
  }
  if (!graphOptions.value.some((g) => g.value === preferred)) {
    return false;
  }
  syncingFromRoute = true;
  try {
    const preferredType = parseTypeCodeFromRoute();
    const graphChanged = graphId.value !== preferred;
    if (graphChanged) {
      setGraphId(preferred);
    }
    state.pageNum = 1;
    await loadTypeOptions();
    if (preferredType) {
      const exists = state.typeOptions.some((t) => t.value === preferredType);
      state.typeCode = exists ? preferredType : null;
    } else if (graphChanged) {
      state.typeCode = null;
    }
    await loadList();
    return true;
  } finally {
    syncingFromRoute = false;
  }
}

const emptyText = computed(() =>
  graphId.value == null ? "请先选择图谱" : "暂无实体"
);

const ALIAS_DISPLAY_MAX = 2;
const TYPE_DISPLAY_MAX = 2;

function normalizeAliases(row) {
  return (row.alias || [])
    .map((item) => (item == null ? "" : String(item).trim()))
    .filter(Boolean);
}

function normalizeTypes(row) {
  return (row.typeNames || [])
    .map((item) => (item == null ? "" : String(item).trim()))
    .filter(Boolean);
}

function displaySlice(list, max) {
  return list.slice(0, max);
}

function extraTagCount(list, max) {
  return Math.max(0, list.length - max);
}

function displayAliases(row) {
  return displaySlice(normalizeAliases(row), ALIAS_DISPLAY_MAX);
}

function extraAliasCount(row) {
  return extraTagCount(normalizeAliases(row), ALIAS_DISPLAY_MAX);
}

function displayTypes(row) {
  return displaySlice(normalizeTypes(row), TYPE_DISPLAY_MAX);
}

function extraTypeCount(row) {
  return extraTagCount(normalizeTypes(row), TYPE_DISPLAY_MAX);
}

function formatTagTooltip(list) {
  return list.length ? list.join("、") : "";
}

const state = reactive({
  loading: false,
  typeCode: null,
  typeOptions: [],
  keywords: "",
  list: [],
  pageNum: 1,
  pageSize: 10,
  total: 0,
  createDrawerVisible: false,
});

async function loadTypeOptions() {
  if (graphId.value == null) {
    state.typeOptions = [];
    return;
  }
  const res = await listEntityCategories(graphId.value);
  if (res?.code === 0) {
    state.typeOptions = (res.data || []).map((c) => ({
      value: c.code,
      label: `${c.name} (${c.code})`,
    }));
  } else {
    state.typeOptions = [];
  }
}

async function onGraphChange() {
  state.typeCode = null;
  state.pageNum = 1;
  await loadTypeOptions();
  await loadList();
}

async function loadList() {
  if (graphId.value == null) {
    state.list = [];
    state.total = 0;
    state.typeOptions = [];
    return;
  }
  state.loading = true;
  try {
    const res = await listAdminEntities({
      knowledgeGraphId: graphId.value,
      keywords: state.keywords || undefined,
      typeCode: state.typeCode || undefined,
      pageNum: state.pageNum,
      pageSize: state.pageSize,
    });
    if (res?.code === 0 && res.data) {
      state.list = res.data.data || [];
      state.total = res.data.total || 0;
      state.pageNum = res.data.pageNum || 1;
    }
  } finally {
    state.loading = false;
  }
}

function handleSearch() {
  state.pageNum = 1;
  loadList();
}

function onPageChange(page) {
  state.pageNum = page;
  loadList();
}

function onEntityCreated() {
  loadList();
}

watch(graphId, async (id, prev) => {
  if (id === prev || syncingFromRoute) {
    return;
  }
  await onGraphChange();
});

watch(
  () => [route.query.graphId, route.query.typeCode],
  async () => {
    if (graphOptions.value.length) {
      await applyGraphFromRoute();
    }
  }
);

onMounted(async () => {
  await ensureGraphOptionsLoaded();
  const applied = await applyGraphFromRoute();
  if (!applied && graphId.value != null) {
    await loadTypeOptions();
    await loadList();
  }
});
</script>
